/**
 * TOBIE collection showcase: rotation, controls, swipe.
 *
 * The progress bar of the current slide is a CSS animation; when it ends the
 * next slide is shown. Pausing that animation therefore pauses the rotation,
 * and everything that should pause it (hover, focus, off screen, hidden tab,
 * the pause button, reduced motion) only has to toggle one class.
 */
(() => {
  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';

    const slides = Array.from(root.querySelectorAll('[data-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-dot]'));
    const viewport = root.querySelector('[data-viewport]');
    const live = root.querySelector('[data-live]');
    const toggle = root.querySelector('[data-toggle]');
    const toggleLabel = root.querySelector('[data-toggle-label]');
    if (slides.length < 2) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canHover = window.matchMedia('(hover: hover)').matches;
    let index = 0;
    let userPaused = reduced;
    let hovering = false;
    let focusing = false;
    let offscreen = false;

    const isPaused = () => userPaused || hovering || focusing || offscreen || document.hidden;

    function sync() {
      root.classList.toggle('is-paused', isPaused());
      root.classList.toggle('is-still', userPaused);
      // Screen readers should hear slide changes only when nothing is changing on its own.
      live.setAttribute('aria-live', isPaused() ? 'polite' : 'off');
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(userPaused));
        toggleLabel.textContent = userPaused ? 'Play' : 'Pause';
      }
    }

    function restartBar() {
      const bar = dots[index] && dots[index].querySelector('.tobie-showcase__bar');
      if (!bar) return;
      bar.style.animation = 'none';
      void bar.offsetWidth; // restart the animation
      bar.style.animation = '';
    }

    function show(next, announce) {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const on = i === index;
        slide.classList.toggle('is-active', on);
        slide.toggleAttribute('inert', !on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      restartBar();
      if (announce) live.textContent = slides[index].getAttribute('aria-label');
      sync();
    }

    // The bar finishing is the signal to move on.
    root.addEventListener('animationend', (event) => {
      if (event.target.classList.contains('tobie-showcase__bar')) show(index + 1, false);
    });

    root.querySelector('[data-prev]').addEventListener('click', () => show(index - 1, true));
    root.querySelector('[data-next]').addEventListener('click', () => show(index + 1, true));
    dots.forEach((dot, i) => dot.addEventListener('click', () => show(i, true)));

    if (toggle) {
      toggle.addEventListener('click', () => {
        userPaused = !userPaused;
        if (!userPaused) restartBar();
        sync();
      });
    }

    if (canHover) {
      root.addEventListener('pointerenter', () => { hovering = true; sync(); });
      root.addEventListener('pointerleave', () => { hovering = false; sync(); });
    }
    root.addEventListener('focusin', () => { focusing = true; sync(); });
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) { focusing = false; sync(); }
    });
    document.addEventListener('visibilitychange', sync);

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { show(index + 1, true); event.preventDefault(); }
      if (event.key === 'ArrowLeft') { show(index - 1, true); event.preventDefault(); }
    });

    // Swipe on touch screens; vertical scrolling is left alone (touch-action: pan-y).
    let startX = null;
    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      startX = event.clientX;
    });
    viewport.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const dx = event.clientX - startX;
      startX = null;
      if (Math.abs(dx) > 50) show(index + (dx < 0 ? 1 : -1), true);
    });
    viewport.addEventListener('pointercancel', () => { startX = null; });

    // Do not rotate while nobody can see it.
    new IntersectionObserver(
      (entries) => { offscreen = !entries.some((entry) => entry.isIntersecting); sync(); },
      { threshold: 0.35 }
    ).observe(root);

    root.classList.add('has-js');
    show(0, false);
  }

  const boot = () => document.querySelectorAll('[data-tobie-showcase]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
